/**
 * Authentication Context and Hook
 * Manages user authentication state with iRacing OAuth
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { User } from '../types';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (returnTo?: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const AUTH_CACHE_KEY = 'iracing_auth_cache';
const AUTH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for OAuth error in URL params (from failed auth)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (oauthError) {
      let friendlyError = decodeURIComponent(oauthError);
      
      // Map common error codes to user-friendly messages
      switch (oauthError) {
        case 'access_denied':
          friendlyError = 'Login was cancelled. Please try again.';
          break;
        case 'invalid_state':
          friendlyError = 'Security check failed (invalid state). Please try again.';
          break;
        case 'missing_pkce_verifier':
          friendlyError = 'Security check failed (missing PKCE). Please try again.';
          break;
        case 'token_exchange_failed':
          friendlyError = 'Failed to connect to iRacing. Please try again.';
          break;
      }
      
      setError(friendlyError);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Redirect to iRacing OAuth login
  const login = useCallback((returnTo?: string) => {
    const baseUrl = '/api/auth/login';
    const target = returnTo || window.location.pathname;
    
    // Don't return to root, return to dashboard if at root
    const safeTarget = target === '/' ? '/dashboard' : target;
    
    window.location.href = `${baseUrl}?returnTo=${encodeURIComponent(safeTarget)}`;
  }, []);

  // Fetch current user session
  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Try to load from cache first
      const cachedAuth = sessionStorage.getItem(AUTH_CACHE_KEY);
      if (cachedAuth) {
        const { user: cachedUser, timestamp } = JSON.parse(cachedAuth);
        if (Date.now() - timestamp < AUTH_CACHE_TTL) {
          setUser(cachedUser);
          setIsLoading(false);
          return; // Exit early, use cached data
        } else {
          sessionStorage.removeItem(AUTH_CACHE_KEY); // Expired cache
        }
      }

      const response = await fetch('/api/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = (await response.json()) as { user: { custId: number; displayName: string } };
        if (data.user) {
          const newUser = {
            custId: data.user.custId,
            displayName: data.user.displayName,
            licenses: [],
          };
          setUser(newUser);
          sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({ user: newUser, timestamp: Date.now() }));
        }
      } else if (response.status === 401) {
        const data = await response.json().catch(() => ({})) as any;
        setUser(null);
        if (data.reauth) {
          login(window.location.pathname + window.location.search);
        }
      } else {
        throw new Error('Failed to fetch user');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [login]);

  // Initial fetch on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Background refresh interval to keep session alive
  // iRacing tokens expire in 10m (access) and 1h (refresh)
  // We check every 5m to trigger backend sliding refresh
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      fetchUser();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [user, fetchUser]);

  // Global unauthorized listener
  useEffect(() => {
    const handleUnauthorized = (event: any) => {
      if (event.detail?.reauth) {
        login(window.location.pathname + window.location.search);
      }
    };
    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, [login]);

  // Global fetch interceptor to catch 401 errors across all API calls
  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      // Check if this is an API call that returned 401
      if (response.status === 401 && args[0] && typeof args[0] === 'string' && args[0].startsWith('/api/')) {
        try {
          const clone = response.clone();
          const data = await clone.json() as { reauth?: boolean };
          if (data.reauth) {
            console.warn('[Auth] 401 with reauth flag detected, redirecting to login...');
            login(window.location.pathname + window.location.search);
          }
        } catch {
          // If we can't parse JSON, just return the original response
        }
      }
      
      return response;
    };
    
    // Cleanup: restore original fetch on unmount
    return () => {
      window.fetch = originalFetch;
    };
  }, [login]);

  // Clear session and redirect
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors, redirect anyway
    }
    setUser(null);
    sessionStorage.removeItem(AUTH_CACHE_KEY); // Clear cache on logout
    window.location.href = '/';
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      error,
      login,
      logout,
      refreshUser: fetchUser,
    }),
    [user, isLoading, error, login, logout, fetchUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
